import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { to55 } from '@/lib/operadoraCoerencia';

// ============================================================================
// OPERADORA DAS LINHAS DA PROPOSTA (Douglas 07/08/2026)
//
// Consulta a operadora real dos números digitados no Montador, para a régua
// produto × operadora barrar na ORIGEM (PN com linha Claro, TT com linha Vivo).
//
// Ordem: cache `telefones_operadora` (60 dias, grátis) → o que faltar vai na
// consulta rápida oficial (`/functions/v1/consulta-operadora`, quick_query,
// lotes de 10 — a MESMA do campo Operadora Doadora do pedido, que também grava
// o cache). Falha da API NÃO derruba o resto: os números sem resposta ficam de
// fora do mapa e a régua não bloqueia por eles ("não verificado").
// ============================================================================

const CACHE_DIAS = 60;
const QUICK_URL = `${import.meta.env.VITE_SUPABASE_URL ?? 'https://beta.crm-api.primetecia.com.br/api'}/functions/v1/consulta-operadora`;

export interface OperadoraLinha {
  operadora: string;
  fonte: 'cache' | 'api';
}

/** `numeros` cru (com ou sem máscara); só 10-11 dígitos entram na consulta. */
/** Mapa estável para o caso "sem resposta ainda": `new Map()` a cada render
 *  troca a identidade e faz quem depende dela recalcular à toa. */
const MAPA_VAZIO: Map<string, OperadoraLinha> = new Map();

export function useOperadoraLinhas(numeros: string[], enabled = true) {
  const t55s = [...new Set(numeros.map(to55).filter(Boolean))] as string[];
  const chave = t55s.slice().sort().join(',');

  const query = useQuery<Map<string, OperadoraLinha>>({
    queryKey: ['operadora-linhas', chave],
    enabled: enabled && t55s.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    // ⛔ A chave é o CONJUNTO de números: terminar um telefone (ou adicionar uma
    // linha que já nasce com número) troca a chave e a query volta ao zero — o
    // selo de TODAS as linhas some e vira "consultando…", cada troca mexe na
    // altura da lista e a tela pula. Segurando o resultado anterior, o selo que
    // já existia continua na tela enquanto o novo chega. (Douglas 02/09/2026)
    placeholderData: (anterior: Map<string, OperadoraLinha> | undefined) => anterior,
    queryFn: async () => {
      const mapa = new Map<string, OperadoraLinha>();

      const cacheValido = new Date(Date.now() - CACHE_DIAS * 24 * 60 * 60 * 1000).toISOString();
      const { data: cacheRows } = await supabase
        .from('telefones_operadora' as never)
        .select('telefone, operadora, ultima_consulta')
        .in('telefone', t55s)
        .gte('ultima_consulta', cacheValido);

      for (const r of ((cacheRows || []) as unknown as Array<{ telefone: string; operadora: string | null }>)) {
        if (r.operadora) mapa.set(r.telefone, { operadora: r.operadora, fonte: 'cache' });
      }

      const faltantes = t55s.filter((t) => !mapa.has(t));
      if (faltantes.length > 0) {
        try {
          const { data: sess } = await supabase.auth.getSession();
          const token = sess?.session?.access_token;
          if (token) {
            for (let i = 0; i < faltantes.length; i += 10) {
              const lote = faltantes.slice(i, i + 10);
              const resp = await fetch(QUICK_URL, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ quick_query: true, numbers: lote }),
                signal: AbortSignal.timeout(20_000),
              });
              if (!resp.ok) break;
              const json = await resp.json();
              for (const r of (json?.results || [])) {
                const num = String(r?.['NÚMERO'] ?? '');
                const op = String(r?.['OPERADORA'] ?? '');
                if (num && op && op !== 'ERRO') mapa.set(num, { operadora: op, fonte: 'api' });
              }
            }
          }
        } catch {
          // API fora/timeout: segue só com o cache — sem resposta = não bloqueia.
        }
      }

      return mapa;
    },
  });

  return {
    /** chave = formato 55DDDN (use `to55` no número da linha para consultar). */
    porTelefone: query.data ?? MAPA_VAZIO,
    // `isFetching` ficava true a cada refetch e piscava o selo inteiro; o que
    // interessa ao consultor é a PRIMEIRA carga, quando ainda não há resposta.
    consultando: query.isLoading,
  };
}
